import { useQuery } from '@tanstack/react-query'
import { gzDeviceQuery, type GzEvent } from '../../api/securiteeGravityZone'
import { Spinner } from '../../ui/Spinner'
import { GzStatusLabel } from './GzStatusLabel'
import './gravityzone.css'

/**
 * Fila GravityZone de pe pagina aparatului: eticheta, cum s-a potrivit,
 * politica, agentul, malware, modulele (cele oprite in rosu numai daca numara
 * la "Issues"), scorul de risc din GravityZone Risk Management si evenimentele
 * primite prin push in ultimele 30 de zile.
 */

function RecentEvents({ events }: { events: GzEvent[] }) {
  return (
    <div className="gz-events">
      <h5>
        <strong>Recent GravityZone events</strong>
      </h5>
      {events.length === 0 ? (
        <p className="gz-muted">No GravityZone events in the last 30 days. They arrive here as they happen when Push is on (Settings › Securitee › GravityZone).</p>
      ) : (
        <div className="gz-events-scroll">
          <table className="table gz-events-table">
            <thead>
              <tr>
                <th>When (UTC)</th>
                <th>Event</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e, i) => (
                <tr key={`${e.at}-${i}`}>
                  <td className="gz-events-at">{e.at}</td>
                  <td>
                    <span className={`label label-${e.severity}`}>{e.type}</span>
                  </td>
                  <td className="gz-events-detail">{e.detail || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
export function DeviceGravityZoneTab({ id }: { id: number }) {
  const query = useQuery(gzDeviceQuery(id))
  if (query.isError) return <div className="alert alert-danger">Could not load the GravityZone data of this device.</div>
  if (!query.data) return <Spinner />
  const { status, endpoint, modules } = query.data
  const events = query.data.events ?? []

  return (
    <div className="gz-device-tab">
      {status && (
        <p>
          <GzStatusLabel status={status} withReasons />
        </p>
      )}
      {endpoint === null && status?.status === 'pending' ? (
        <p className="gz-muted">This company has not been read from GravityZone yet. Sync now in Settings › Securitee › GravityZone starts it.</p>
      ) : endpoint === null ? (
        <p className="gz-muted">no GravityZone endpoint matches this device (by MAC address or by name).</p>
      ) : (
        <>
          <table className="table gz-device">
            <tbody>
              <tr>
                <th scope="row">GravityZone endpoint</th>
                <td>
                  {endpoint.name}
                  {endpoint.match_method && (
                    <span className="gz-muted">
                      {' '}
                      · {endpoint.match_method === 'manual' ? 'matched manually' : `matched by ${endpoint.match_method === 'mac' ? 'MAC' : 'name'}`}
                    </span>
                  )}
                </td>
              </tr>
              <tr>
                <th scope="row">Policy</th>
                <td>
                  {endpoint.policy_name ?? '—'}
                  {endpoint.policy_applied !== null && <span className={endpoint.policy_applied ? '' : 'gz-module-off'}> · {endpoint.policy_applied ? 'applied' : 'not applied'}</span>}
                </td>
              </tr>
              <tr>
                <th scope="row">Agent</th>
                <td>
                  {endpoint.agent_version ?? '—'}
                  {endpoint.signatures_at && <span className="gz-muted"> · signatures updated {endpoint.signatures_at}</span>}
                  {endpoint.signatures_outdated && <span className="gz-module-off"> · signatures outdated</span>}
                </td>
              </tr>
              <tr>
                <th scope="row">Last seen in GZ</th>
                <td>
                  {endpoint.last_seen_at ?? '—'}
                  {endpoint.online !== null && <span className="gz-muted"> · {endpoint.online ? 'online' : 'offline'}</span>}
                </td>
              </tr>
              <tr>
                <th scope="row">Malware</th>
                <td className={endpoint.malware_infected ? 'gz-module-off' : ''}>
                  {endpoint.malware_infected === null ? '—' : endpoint.malware_infected ? 'active infection' : 'none detected'}
                </td>
              </tr>
              <tr>
                <th scope="row">Risk</th>
                <td>
                  {endpoint.risk.score === null ? (
                    '— (GravityZone Risk Management not available)'
                  ) : (
                    <>
                      {endpoint.risk.score}% {endpoint.risk.impact && <span className="gz-muted">({endpoint.risk.impact})</span>}
                      <span className="gz-muted">
                        {' '}
                        · misconfigurations {endpoint.risk.misconfig ?? '—'}% · app vulnerabilities {endpoint.risk.app_vuln ?? '—'}% · human risk {endpoint.risk.human ?? '—'}%
                      </span>
                    </>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
          {modules.length > 0 && (
            <>
              <h5>
                <strong>Protection modules</strong>
              </h5>
              <ul className="gz-modules">
                {modules.map((m) => (
                  <li key={m.key} className={!m.on && m.essential ? 'gz-module-off' : ''}>
                    {m.label}: {m.on ? 'On' : 'Off'}
                    {!m.on && m.essential && <span> (counts as an issue)</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
          <p className="gz-muted">Read from GravityZone at {endpoint.synced_at}.</p>
          <RecentEvents events={events} />
        </>
      )}
    </div>
  )
}
